import { Layout } from 'antd'
import PropTypes from 'prop-types'
import React from 'react'
import { Redirect, Route, Switch, withRouter } from 'react-router-dom'
import * as settingsConstants from '../dux/constants/settings'
import { isContentAdmin } from '../dux/modules/utils'
import { canViewAnalytics, isAppOnlyUser } from '../dux/modules/appAccess'
import { redirectToApp } from '../dux/modules/ssoHandoff'
import { recordLoginToDashboard } from '../dux/modules/rum'
import BamHeaderContainer from './BamHeaderContainer'
import ChunkErrorBoundary from './common/ChunkErrorBoundary'
import FourOhFour from './common/FourOhFour'
import PrivacyPolicy from './common/PrivacyPolicy'
import SuspenseFallback from './common/SuspenseFallback'
import Sidebar from './sidebar'
import LoadingModal from './modals/LoadingModal'
import ModalContainer from './modals/ModalContainer'
import './styles/Layout.css'

// Phase 1 lazy splits: admin/low-traffic routes
const Analytics = React.lazy(() => import('./analytics/Analytics'))
const BuilderRouter = React.lazy(() => import('./builders/BuilderRouter'))
const NotificationsContainer = React.lazy(
  () => import('./notifications/NotificationsContainer'),
)
const GroupsContainer = React.lazy(
  () => import('./permissions/GroupsContainer'),
)
const InvitationContainer = React.lazy(
  () => import('./permissions/InvitationContainer'),
)
const RolesContainer = React.lazy(() => import('./permissions/RolesContainer'))
const Superuser = React.lazy(() => import('./permissions/Superuser'))
const UsersContainer = React.lazy(() => import('./permissions/UsersContainer'))
const RegistrationApproval = React.lazy(
  () => import('./permissions/RegistrationApproval'),
)
const MSTBrandingSettings = React.lazy(
  () => import('./settings/MSTBrandingSettings'),
)
const MSTFeaturedAssetsSettings = React.lazy(
  () => import('./settings/MSTFeaturedAssetsSettings'),
)
const MSTMenuSettings = React.lazy(() => import('./settings/MSTMenuSettings'))
const MSTResourceSettings = React.lazy(
  () => import('./settings/MSTResourceSettings'),
)
const SettingsContainer = React.lazy(
  () => import('./settings/SettingsContainer'),
)
const SharedEndpointSettings = React.lazy(
  () => import('./settings/SharedEndpointSettings'),
)

// Phase 3 & 4 lazy splits: core private routes
const Home = React.lazy(() => import('./library/Home'))
const AssetDetail = React.lazy(() => import('./library/AssetDetail'))
const AssetCrop = React.lazy(() => import('./library/AssetCrop'))
const UserProfile = React.lazy(() => import('./profile/UserProfile'))
const AssetDetailSidebar = React.lazy(
  () => import('./library/sidebar/AssetDetailSidebar'),
)
const SettingsSidebar = React.lazy(() => import('./settings/SettingsSidebar'))

// Deep links (a shared asset, a notification) still open where they point
const LANDING_PATHS = ['/', '/login', '/library']

class PrivateApp extends React.Component {
  componentDidMount() {
    recordLoginToDashboard()
    this.sendAppOnlyUserToApp()
  }

  componentDidUpdate() {
    this.sendAppOnlyUserToApp()
  }

  sendAppOnlyUserToApp() {
    const { permissionsLoaded, superuser, email, groups, allFolders, config } =
      this.props
    if (this.sentToApp || !permissionsLoaded || superuser || config.mask) return
    if (!LANDING_PATHS.includes(window.location.pathname)) return
    if (!isAppOnlyUser(email, groups, allFolders)) return
    this.sentToApp = true
    redirectToApp(config)
  }

  render() {
    const { Header, Sider, Content } = Layout
    const { analytics, notifications } = this.props.modules

    if (this.props.maintenance?.state === 1 && !this.props.superuser) {
      return <Redirect to="/maintenance" />
    } else if (this.props.versionMismatch) {
      return <Redirect to="/maintenance" />
    }
    return (
      <Layout className="bam-layout" style={{ height: '100vh' }}>
        <Header className="bam-header">
          <BamHeaderContainer logoWidth={this.props.width} />
        </Header>
        <Layout className="bam-main-layout">
          <Sider className="bam-sider" width={this.props.width} trigger={null}>
            <React.Suspense fallback={null}>
              <Switch>
                <Route path="/settings" component={SettingsSidebar} />
                <Route path="/asset/crop/:id" component={AssetDetailSidebar} />
                <Route path="/asset/:id" component={AssetDetailSidebar} />
                <Route path="*" component={Sidebar} />
              </Switch>
            </React.Suspense>
          </Sider>
          <Content className="bam-main-content" id="main-content">
            <ChunkErrorBoundary>
              <React.Suspense fallback={<SuspenseFallback />}>
                <Route
                  exact
                  path="/login"
                  render={() => (
                    <Redirect to={this.props.startupRoute || '/'} />
                  )}
                />
                <Route
                  exact
                  path="/"
                  render={() => {
                    this.props.actions.setMenuItem('library')
                    return <Redirect to="/library" />
                  }}
                />
                <Switch>
                  {/* The DAM's own dashboard is retired -- the app (mp web) owns
                      it now. Kept as a redirect for bookmarks and for the four
                      tenants whose modules.dashboard.enabled.web is still on. */}
                  <Route
                    exact
                    path="/dashboard"
                    render={() => {
                      this.props.actions.setMenuItem('library')
                      return <Redirect to={'/library'} />
                    }}
                  />
                  <Route
                    path="/register"
                    render={() => <Redirect to="/library" />}
                  />
                  <Route
                    path="/analytics"
                    render={() =>
                      !this.props.permissionsLoaded ? (
                        <SuspenseFallback />
                      ) : analytics &&
                        !analytics.disabled &&
                        canViewAnalytics(this.props.email, this.props.groups) ? (
                        <Analytics />
                      ) : (
                        <Redirect to={'/library'} />
                      )
                    }
                  />
                  <Route
                    path="/library/notifications"
                    render={() =>
                      notifications ? (
                        <NotificationsContainer />
                      ) : (
                        <Redirect to="/library" />
                      )
                    }
                  />
                  <Route path="/asset/crop/:id" component={AssetCrop} />
                  <Route path="/asset/:id" component={AssetDetail} />
                  <Redirect
                    from="/library/asset/crop/:id"
                    to="/asset/crop/:id"
                  />
                  <Redirect from="/library/asset/:id" to="/asset/:id" />
                  <Route path="/library/:folder" component={Home} />
                  <Route exact path="/library" component={Home} />
                  <Route
                    path="/builders"
                    render={() =>
                      !this.props.permissionsLoaded ? (
                        <SuspenseFallback />
                      ) : isContentAdmin(
                          this.props.email,
                          this.props.groups,
                        ) ? (
                        <BuilderRouter {...this.props} />
                      ) : (
                        <Redirect to={'/library'} />
                      )
                    }
                  />
                  <Route
                    path="/settings/permissions/invitations"
                    component={InvitationContainer}
                  />
                  <Route
                    path="/settings/permissions/users"
                    component={UsersContainer}
                  />
                  <Route
                    path="/settings/permissions/approval"
                    component={RegistrationApproval}
                  />
                  <Route
                    path="/settings/permissions/groups"
                    component={GroupsContainer}
                  />
                  <Route
                    path="/settings/permissions/roles"
                    component={RolesContainer}
                  />
                  <Route
                    path="/settings/mst/branding"
                    component={MSTBrandingSettings}
                  />
                  <Route
                    path="/settings/mst/featured"
                    component={MSTFeaturedAssetsSettings}
                  />
                  <Route
                    path="/settings/mst/menu"
                    component={MSTMenuSettings}
                  />
                  <Route
                    path="/settings/mst/resources"
                    component={MSTResourceSettings}
                  />
                  <Redirect from="/settings/mst/uploads" to="/settings" />
                  <Route
                    path="/settings/dam/general"
                    render={() => <SettingsContainer setting="general" />}
                  />
                  <Route
                    path="/settings/dam/builders"
                    render={() => <SettingsContainer setting="builders" />}
                  />
                  <Route
                    path="/settings/dam/profiles"
                    render={() => <SettingsContainer setting="profiles" />}
                  />
                  <Route
                    path="/settings/dam/shared-endpoints"
                    component={SharedEndpointSettings}
                  />
                  <Redirect
                    from="/settings/integrations/salesforce"
                    to="/settings"
                  />
                  <Redirect
                    exact
                    from="/settings"
                    to="/settings/mst/branding"
                  />
                  <Redirect from="/notifications" to="/library/notifications" />
                  <Route path="/profile" component={UserProfile} />
                  <Route
                    path="/superuser"
                    render={() =>
                      this.props.superuser ? (
                        <Superuser client={this.props.client} />
                      ) : (
                        <FourOhFour />
                      )
                    }
                  />
                  <Route
                    path="/reset-password"
                    render={() => <Redirect to="/library" />}
                  />
                  <Route path="/privacy" component={PrivacyPolicy} />
                  <Route path="*" component={FourOhFour} />
                </Switch>
              </React.Suspense>
            </ChunkErrorBoundary>
          </Content>
          <ModalContainer />
          <LoadingModal loading={this.props.pending} />
        </Layout>
      </Layout>
    )
  }
}

PrivateApp.propTypes = {
  superuser: PropTypes.bool,
  startupRoute: PropTypes.string,
  allFolders: PropTypes.object,
  folderActions: PropTypes.object,
  actions: PropTypes.object,
  pending: PropTypes.bool,
  width: PropTypes.number,
  sidebarCollapsed: PropTypes.bool,
  onSidebarToggle: PropTypes.func,
  onLoad: PropTypes.func,
  groups: PropTypes.object,
  permissionsLoaded: PropTypes.bool,
  email: PropTypes.string,
  location: PropTypes.object,
  maintenance: PropTypes.object,
}

export default withRouter(PrivateApp)
